import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarCheck,
  faCreditCard,
  faStore,
  faUsers,
  faChartLine,
  faBell,
  faMobileScreen,
  faStar,
  faShieldHalved,
  faLanguage,
  faReceipt,
  faPalette,
  faGlobe,
  faCartShopping,
  faGift,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons'
import { PublicLayout } from '../components/PublicLayout'
import { HomeHeroLanding } from '../components/HomeHeroLanding'
import { BookingStepsPath } from '../components/BookingStepsPath'
import { HomeBranchesSection } from '../components/HomeBranchesSection'
import { HomeGoogleReviews } from '../components/HomeGoogleReviews'
import { salonApi, saasApi, homepageApi } from '../lib/api'
import { HERO_SLIDES, mapHeroSlideDto } from '../lib/heroSlides'
import { planImageSrc } from '../lib/planMedia'
import './HomePage.css'

const ILIA_SLUG = 'ilia-barbershop'

export function HomePage() {
  const { t, i18n } = useTranslation()
  const plansQ = useQuery({ queryKey: ['saas-plans'], queryFn: saasApi.plans })
  const heroSlidesQ = useQuery({
    queryKey: ['homepage-hero-slides'],
    queryFn: () => homepageApi.heroSlides(),
    staleTime: 60_000,
  })
  const branchesQ = useQuery({
    queryKey: ['branches', ILIA_SLUG],
    queryFn: () => salonApi.branches(ILIA_SLUG),
  })
  const employeesQ = useQuery({
    queryKey: ['employees', ILIA_SLUG],
    queryFn: () => salonApi.employees(ILIA_SLUG),
  })

  const branches = branchesQ.data?.data ?? []
  const barberCount = (employeesQ.data?.data ?? []).filter((e) => e.isBookable).length
  const heroSlides =
    heroSlidesQ.data?.data?.length
      ? heroSlidesQ.data.data.map((dto) => mapHeroSlideDto(dto, i18n.language))
      : HERO_SLIDES

  return (
    <PublicLayout>
      <HomeHeroLanding
        brand={t('hero.brand')}
        line1={t('hero.line1')}
        line2={t('hero.line2')}
        line3={t('hero.line3')}
        primaryCta={{ label: t('hero.ctaBook'), to: '/book' }}
        secondaryCta={{ label: t('hero.ctaSubscribe'), to: '/subscribe' }}
        stats={[
          { value: '10+', label: t('homeBranches.statYears') },
          { value: barberCount > 0 ? `${barberCount}+` : '5+', label: t('homeBranches.statBarbers') },
          { value: String(branches.length || 2), label: t('homeBranches.statBranches') },
        ]}
        slides={heroSlides}
      />

      <BookingStepsPath
        title={t('hero.stepsTitle')}
        steps={([1, 2, 3, 4, 5] as const).map((n) => ({
          id: n,
          label: t(`howSteps.${n}`),
          description: t(`hero.stepDesc${n}`),
        }))}
      />

      <HomeBranchesSection
        branches={branches}
        loading={branchesQ.isLoading}
        error={branchesQ.isError}
      />

      <HomeGoogleReviews />

      <section className="section muted plans-section" id="plans">
        <div className="container">
          <header className="plans-intro">
            <p className="eyebrow">{t('plans.eyebrow')}</p>
            <h2>{t('plans.title')}</h2>
            <p className="lead">{t('plans.lead')}</p>
            <ul className="plans-perks">
              {(t('plans.perks', { returnObjects: true }) as string[]).map((perk, i) => (
                <li key={i}><span className="plans-perk-dot" />{perk}</li>
              ))}
            </ul>
          </header>
          <div className="plans">
            {(plansQ.data?.data ?? []).map((plan, index) => (
              <article
                key={plan.id}
                className={`plan ${plan.isFeatured ? 'recommended' : ''}`}
                style={{ ['--plan-i' as string]: index }}
              >
                <div className="plan-media">
                  <img
                    src={planImageSrc(plan)}
                    alt={t('plans.imageAlt', { name: plan.name })}
                    loading="lazy"
                  />
                  {plan.isFeatured && <em>{t('plans.recommended')}</em>}
                </div>
                <div className="plan-body">
                  <h3>{plan.name}</h3>
                  <p className="price"><strong>{plan.monthlyPrice} €</strong> {t('plans.perMonth')}</p>
                  <p>{t('plans.total')}: {plan.contractTotal} €</p>
                  <p className="vat">{plansQ.data?.vatNote ?? t('plans.vatNote')}</p>
                  <div className="hero-cta">
                    <Link className="btn btn-primary" to={`/subscribe?plan=${plan.id}&wizard=1`}>{t('plans.request')}</Link>
                    <Link className="btn btn-ghost" to={`/plans/${plan.id}`}>{t('plans.details')}</Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section features-section" id="features">
        <div className="container">
          <div className="features-header">
            <p className="eyebrow">{t('sections.features')}</p>
            <h2>{t('sections.features')}</h2>
          </div>
          {(() => {
            const features = [
              { icon: faCalendarCheck, label: 'Online-Buchung', labelAr: 'حجز أونلاين' },
              { icon: faUsers, label: 'Mitarbeiter', labelAr: 'إدارة الموظفين' },
              { icon: faStore, label: 'Filialen', labelAr: 'الفروع' },
              { icon: faCreditCard, label: 'Zahlungen', labelAr: 'المدفوعات' },
              { icon: faChartLine, label: 'Berichte', labelAr: 'التقارير' },
              { icon: faBell, label: 'Benachrichtigungen', labelAr: 'الإشعارات' },
              { icon: faMobileScreen, label: 'Alle Geräte', labelAr: 'جميع الأجهزة' },
              { icon: faStar, label: 'Mitgliedschaften', labelAr: 'العضويات' },
              { icon: faCartShopping, label: 'Shop & Produkte', labelAr: 'المتجر' },
              { icon: faGift, label: 'Treuepunkte', labelAr: 'نقاط الولاء' },
              { icon: faReceipt, label: 'Rechnungen', labelAr: 'الفواتير' },
              { icon: faShieldHalved, label: 'DSGVO-konform', labelAr: 'حماية البيانات' },
              { icon: faLanguage, label: 'Mehrsprachig', labelAr: 'متعدد اللغات' },
              { icon: faPalette, label: 'Eigenes Branding', labelAr: 'تخصيص العلامة' },
              { icon: faGlobe, label: 'Eigene Domain', labelAr: 'دومين مخصص' },
              { icon: faHeadset, label: 'Support', labelAr: 'الدعم الفني' },
            ] as const;
            const doubled = [...features, ...features];
            return (
              <div className="features-slider">
                <div className="features-track">
                  {doubled.map((item, i) => (
                    <div key={i} className="feature-slide">
                      <div className="feature-slide-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <span>{i18n.language === 'ar' ? item.labelAr : item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

    </PublicLayout>
  )
}
